/** Compact intelligence card — Oracle's operational summaries. */
import React from "react";
import { ArrowRight } from "lucide-react";

const TONES = {
  ok: "#3FB950", warn: "#E0A526", alert: "#D92D20",
  accent: "#D92D20", notice: "#9FA8D4",
};

export const IntelCard = ({ card, onNavigate, onAction }) => {
  if (!card) return null;
  return (
    <div className="border border-[#2D2A26] bg-[#0a0806] mt-2" data-testid="intel-card">
      <div className="px-3 py-1.5 border-b border-[#D92D20]/25 flex items-center gap-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20]">
          {card.title}
        </span>
        {card.note && (
          <span className="ml-auto font-mono text-[8px] uppercase tracking-[0.2em] text-[#E0A526]"
                data-testid="intel-card-note">
            {card.note}
          </span>
        )}
      </div>
      <div className="px-3 py-2 grid gap-1">
        {(card.rows || []).map((r, i) => {
          const color = TONES[r.tone] || "#C9C2B4";
          const inner = (
            <>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#7B7168] shrink-0">
                {r.label}
              </span>
              <span className="flex-1 border-b border-dotted border-[#2D2A26] mx-2 translate-y-[-3px]" />
              <span className="font-mono text-[11px] text-right" style={{ color }}>
                {r.value}
              </span>
            </>
          );
          return r.url ? (
            <button key={i} type="button" data-testid={`intel-card-row-${i}`}
                    onClick={() => (r.url === "__brief__" ? onAction?.(r.url) : onNavigate?.(r.url))}
                    className="flex items-baseline w-full text-left hover:bg-[#181008] px-1 -mx-1 transition-colors">
              {inner}
            </button>
          ) : (
            <div key={i} data-testid={`intel-card-row-${i}`} className="flex items-baseline px-1 -mx-1">
              {inner}
            </div>
          );
        })}
      </div>
      {card.dims && (
        <div className="px-3 py-2 border-t border-[#2D2A26] grid grid-cols-2 gap-x-5 gap-y-1.5"
             data-testid="intel-dims">
          {card.dims.map((d) => {
            const c = d.score >= 70 ? "#3FB950" : d.score >= 40 ? "#E0A526" : "#D92D20";
            return (
              <div key={d.label}>
                <div className="flex justify-between font-mono text-[8px] uppercase tracking-[0.12em] text-[#7B7168]">
                  <span>{d.label}</span>
                  <span style={{ color: c }}>{d.score}</span>
                </div>
                <div className="h-[3px] bg-[#1C1917] mt-0.5">
                  <div className="h-full" style={{ width: `${Math.min(100, d.score)}%`, background: c }} />
                </div>
                {d.action && (
                  <button type="button"
                          onClick={() => onNavigate?.(d.action.url)}
                          data-testid={`dim-action-${d.label.replace(/\s+/g, "-").toLowerCase()}`}
                          className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.1em] text-[#D92D20]/80 hover:text-[#D92D20]">
                    {d.action.label} →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {card.footer && (
        <button type="button" data-testid="intel-card-footer"
                onClick={() => (card.footer.url === "__brief__"
                  ? onAction?.(card.footer.url) : onNavigate?.(card.footer.url))}
                className="w-full px-3 py-1.5 border-t border-[#2D2A26] flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39E93] hover:text-[#D92D20] transition-colors">
          {card.footer.label} <ArrowRight size={10} />
        </button>
      )}
    </div>
  );
};

export default IntelCard;
